// src/components/account/RecurringOrdersSection.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './RecurringOrdersSection.css';

/* ---------------- API ---------------- */
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const apiUrl = (p) => (API_BASE ? `${API_BASE}${p}` : p);

const ENDPOINTS = {
  list:     (userId)        => apiUrl(`/api/recurring-orders/${userId}`),
  update:   (id)            => apiUrl(`/api/recurring-orders/${id}`),
  remove:   (id)            => apiUrl(`/api/recurring-orders/${id}`),
  patchItem:(id, productId) => apiUrl(`/api/recurring-orders/${id}/items/${productId}`),
};

/* ---------------- Apurit ---------------- */
const centsToEUR = (c) =>
  ((Number.isFinite(c) ? c : 0) / 100).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });

const fmtDate = (d) => {
  const date = d ? new Date(d) : null;
  return date && !isNaN(date.valueOf())
    ? date.toLocaleDateString('fi-FI', { timeZone: 'Europe/Helsinki' })
    : '—';
};

const toISODate = (d) => {
  const dd = d ? new Date(d) : new Date();
  dd.setHours(0,0,0,0);
  const y = dd.getFullYear();
  const m = String(dd.getMonth() + 1).padStart(2, '0');
  const da = String(dd.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};
const parseISODate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : new Date());
const clampWeeks = (n) => Math.max(1, Math.min(12, Number(n) || 1));

// Sunnuntain cutoff = 23:50 paikallista aikaa
function nextFrom(anchorSundayStart, weeks, now = new Date()) {
  const intervalMs = weeks * 7 * 24 * 60 * 60 * 1000;
  const cutoff = new Date(anchorSundayStart);
  cutoff.setHours(23, 50, 0, 0); // su 23:50

  if (now <= cutoff) return new Date(anchorSundayStart);

  const delta = now.getTime() - cutoff.getTime();
  const k = Math.ceil(delta / intervalMs);
  return new Date(anchorSundayStart.getTime() + k * intervalMs);
}

/* ISO-week apurit */
function dateToISOWeekStringLocal(date) {
  const d = new Date(date);
  d.setHours(0,0,0,0);
  const th = new Date(d);
  th.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(th.getFullYear(), 0, 4);
  const week = 1 + Math.round(((th - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  const year = th.getFullYear();
  return `${year}-W${String(week).padStart(2, '0')}`;
}
function isoWeekStartLocal(year, week) {
  const jan4 = new Date(year, 0, 4);
  jan4.setHours(0,0,0,0);
  const day = jan4.getDay() || 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - day + 1 + (week - 1) * 7);
  mon.setHours(0,0,0,0);
  return mon;
}
function isoWeekToSundayISO(weekStr) {
  if (!weekStr || !weekStr.includes('-W')) return toISODate(new Date());
  const [yPart, wPart] = weekStr.split('-W');
  const year = parseInt(yPart, 10);
  const week = parseInt(wPart, 10);
  if (!Number.isFinite(year) || !Number.isFinite(week)) return toISODate(new Date());
  const mon = isoWeekStartLocal(year, week);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return toISODate(sun);
}

/* Seuraavat n lähetyspäivää asetuksilla */
function nextOccurrences(anchorSundayISO, weeks, count = 4, now = new Date()) {
  const firstAnchor = parseISODate(anchorSundayISO);
  const first = nextFrom(firstAnchor, weeks, now);
  const out = [];
  let d = first;
  for (let i = 0; i < count; i++) {
    out.push(new Date(d));
    d = new Date(d.getTime() + weeks * 7 * 24 * 60 * 60 * 1000);
  }
  return out;
}

/* Muunnokset */
function euroLikeToCents(v) {
  if (v === Infinity) return Infinity;
  if (v === null || v === undefined) return 0;
  const n = Number(String(v).replace(',', '.'));
  if (!Number.isFinite(n)) return 0;
  return n < 1000 ? Math.round(n * 100) : Math.round(n);
}

/* ---------- joustava item-poiminta ---------- */
function pickOne(obj, keys, fallback) {
  for (const k of keys) {
    const v = k.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return fallback;
}
function readQty(it) {
  const v = pickOne(it, ['quantity', 'qty', 'amount', 'count', 'orderedQty'], 1);
  const n = Number(v);
  return Number.isFinite(n) ? n : 1;
}
function readProductId(it) {
  return it.productId || it.supplierProductId || it.product?.id || it.product?._id || it._productId || it.sku || null;
}
function readName(it, cache) {
  const fromItem = pickOne(
    it,
    ['name','productName','title','displayName','product.displayName','product.name','product.title'],
    null
  );
  if (fromItem) return fromItem;
  const id = readProductId(it);
  if (id && cache[id]?.name) return cache[id].name;
  return 'Tuote';
}
function readPack(it, cache) {
  const fromItem = pickOne(
    it,
    ['packsize','packSize','packageSize','unit','product.packsize','product.packSize','product.packageSize'],
    null
  );
  if (fromItem) return fromItem;
  const id = readProductId(it);
  return id && cache[id]?.pack ? cache[id].pack : '';
}
function readUnitPriceCents(it, cache) {
  const c = pickOne(it, ['unitPriceCents','priceCents','netPriceCents','grossPriceCents'], null);
  if (Number.isFinite(c)) return c;
  const euro = pickOne(it, ['unitPrice','price','unit_price','product.unitPrice','product.price'], null);
  if (euro !== null && euro !== undefined) {
    const n = Number(String(euro).replace(',', '.'));
    if (Number.isFinite(n)) return n < 1000 ? Math.round(n * 100) : Math.round(n);
  }
  const id = readProductId(it);
  if (id && Number.isFinite(cache[id]?.unitPriceCents)) return cache[id].unitPriceCents;
  return 0;
}

/* ---------------- Pienet UI-komponentit ---------------- */
function StatusBadge({ status = 'active' }) {
  const label = status === 'cancelled' ? 'Peruttu' : status === 'paused' ? 'Tauolla' : 'Aktiivinen';
  const cls = status === 'active' ? 'ro-badge ro-badge--green'
            : status === 'paused' ? 'ro-badge ro-badge--amber'
            : 'ro-badge ro-badge--gray';
  return <span className={cls}>{label}</span>;
}

function Collapsible({ children, labelOpen, labelClosed }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ro-collapsible">
      <button type="button" className="ro-link" onClick={() => setOpen(!open)} aria-expanded={open}>
        {open ? labelOpen : labelClosed}
      </button>
      {open && <div className="ro-collapsible__content">{children}</div>}
    </div>
  );
}

function WeeksSelect({ value, onChange, disabled }) {
  return (
    <select
      className="ro-input ro-select"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
      aria-label="Toistuvuus viikkoina"
    >
      {Array.from({ length: 12 }, (_, i) => i + 1).map((w) => (
        <option key={w} value={w}>{w} vko{w > 1 ? 'a' : ''}</option>
      ))}
    </select>
  );
}

function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <div className="ro-error">
      <div className="ro-error__text">{message}</div>
      {onRetry && <button className="ro-error__retry" onClick={onRetry}>Yritä uudelleen</button>}
    </div>
  );
}

function useToast() {
  const [msg, setMsg] = useState('');
  useEffect(() => { if (!msg) return; const t = setTimeout(() => setMsg(''), 2500); return () => clearTimeout(t); }, [msg]);
  const Toast = () => (msg ? <div className="ro-toast" role="status" aria-live="polite">{msg}</div> : null);
  return { setToast: setMsg, Toast };
}

/* ---------------- Pääkomponentti ---------------- */
export default function RecurringOrdersSection() {
  const { isReady, user, getAuthHeaders } = useAuth();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [supplierInfo, setSupplierInfo] = useState({});
  const [productCache, setProductCache] = useState({});
  const [pending, setPending] = useState({});           // { [id]: { weeks, weekStr } }

  // Rivikohtainen busy-indikaattori tallennuksen aikana
  const [pendingLine, setPendingLine] = useState({});   // { [`${recId}:${productId}`]: true }

  // UUSI: alkuperäiset määrät ja "dirty"-tila
  const [originalQty, setOriginalQty] = useState({});   // { [recId]: { [productId]: qty } }
  const [dirty, setDirty] = useState({});               // { [recId]: true }

  const { setToast, Toast } = useToast();
  const hasAny = useMemo(() => rows.length > 0, [rows.length]);

  const onChangeWeeks = (id, val) =>
    setPending((p) => ({ ...p, [id]: { ...p[id], weeks: clampWeeks(Number(val)) } }));
  const onChangeWeekStr = (id, weekStr) =>
    setPending((p) => ({ ...p, [id]: { ...p[id], weekStr: weekStr || dateToISOWeekStringLocal(new Date()) } }));

  async function loadList(uid) {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(ENDPOINTS.list(uid), { headers: { ...getAuthHeaders() } });
      const list = Array.isArray(data) ? data
        : Array.isArray(data?.items) ? data.items
        : Array.isArray(data?.orders) ? data.orders
        : Array.isArray(data?.data) ? data.data : [];
      setRows(list);

      const init = {};
      list.forEach((rec) => {
        const anchor = rec.anchorDate || rec.nextOrderDate || rec.createdAt || new Date();
        init[rec._id] = {
          weeks: Number.isFinite(rec.frequencyWeeks) ? rec.frequencyWeeks : 4,
          weekStr: dateToISOWeekStringLocal(anchor),
        };
      });
      setPending(init);

      const sids = [...new Set(list.map((x) => String(x.supplierId)).filter(Boolean))];
      if (sids.length) {
        const map = {};
        await Promise.all(sids.map(async (id) => {
          try {
            const r = await axios.get(apiUrl(`/api/suppliers/${id}`));
            map[id] = r?.data || { id, displayName: String(id) };
          } catch { map[id] = { id, displayName: String(id) }; }
        }));
        setSupplierInfo(map);
      } else setSupplierInfo({});

      const allItems = list.flatMap((rec) =>
        Array.isArray(rec.products) ? rec.products : Array.isArray(rec.items) ? rec.items : []
      );
      const ids = [...new Set(allItems.map(readProductId).filter(Boolean))];
      if (ids.length) {
        const updates = {};
        await Promise.all(ids.map(async (id) => {
          const tries = [
            apiUrl(`/api/products/${id}`),
            apiUrl(`/api/supplier-products/${id}`),
            apiUrl(`/api/catalog/products/${id}`),
          ];
          for (const url of tries) {
            try {
              const r = await axios.get(url);
              const p = r?.data || {};
              const name = p.displayName || p.name || p.title || p.product?.displayName || p.product?.name || String(id);
              const pack = p.packsize || p.packSize || p.packageSize || p.unit || p.product?.packsize || p.product?.packSize || '';
              let unitPriceCents =
                (Number.isFinite(p.unitPriceCents) && p.unitPriceCents) ||
                (Number.isFinite(p.priceCents) && p.priceCents) || 0;
              if (!unitPriceCents) {
                const euro = p.unitPrice ?? p.price ?? p.product?.unitPrice ?? p.product?.price ?? null;
                if (euro !== null && euro !== undefined) {
                  const n = Number(String(euro).replace(',', '.'));
                  if (Number.isFinite(n)) unitPriceCents = n < 1000 ? Math.round(n * 100) : Math.round(n);
                }
              }
              updates[id] = { name, pack, unitPriceCents };
              break;
            } catch { /* try next */ }
          }
        }));
        if (Object.keys(updates).length) setProductCache((prev) => ({ ...prev, ...updates }));
      }

      // Tallenna alkuperäiset määrät (diffiä varten)
      const oq = {};
      list.forEach((rec) => {
        const its = Array.isArray(rec.products) ? rec.products
                  : Array.isArray(rec.items)    ? rec.items : [];
        const map = {};
        its.forEach(it => {
          const pid = readProductId(it);
          if (pid) map[pid] = readQty(it);
        });
        oq[rec._id] = map;
      });
      setOriginalQty(oq);
      setDirty({});

    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Toistuvien tilausten haku epäonnistui.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isReady || !user) return;
    const uid = user.id || user._id;
    loadList(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, user, getAuthHeaders]);

  /* ---------- Paikallinen määrämuutos ilman backend-kutsua ---------- */
  function keyFor(recId, productId){ return `${recId}:${productId}`; }

  function updateLocalQty(recId, productId, newQty) {
    setRows((prev) => prev.map((rec) => {
      if (rec._id !== recId) return rec;
      const key = Array.isArray(rec.products) ? 'products'
                : Array.isArray(rec.items)    ? 'items' : null;
      if (!key) return rec;

      const arr = [...rec[key]];
      const idx = arr.findIndex(it => String(readProductId(it)) === String(productId));
      if (idx === -1) return rec;

      if (newQty <= 0) {
        arr.splice(idx, 1);
      } else {
        const base = arr[idx];
        arr[idx] = {
          ...base,
          quantity: newQty,
          qty: newQty,
          amount: newQty,
          count: newQty,
          orderedQty: newQty,
        };
      }
      return { ...rec, [key]: arr };
    }));
    setDirty((d) => ({ ...d, [recId]: true }));
  }

  async function handleIncQty(rec, productId, delta, currentQty) {
    const next = currentQty + delta;
    if (next <= 0) {
      const ok = window.confirm('Poistetaanko tämä rivi toistuvasta tilauksesta?');
      if (!ok) return;
      updateLocalQty(rec._id, productId, 0);
      setToast('Rivi poistettu (ei vielä tallennettu).');
      return;
    }
    updateLocalQty(rec._id, productId, next);
  }

  async function handleRemoveLine(rec, productId) {
    const ok = window.confirm('Poistetaanko tämä rivi toistuvasta tilauksesta?');
    if (!ok) return;
    updateLocalQty(rec._id, productId, 0);
    setToast('Rivi poistettu (ei vielä tallennettu).');
  }

  /* ---------- Tallennus: ensin rivimuutokset (diff), sitten aikataulu ---------- */
  function applyUpdatedRecurringToState(updated) {
    if (!updated || !updated._id) return;
    setRows((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));
  }

  async function patchRecurringItem(recId, productId, payload) {
    const k = keyFor(recId, productId);
    setPendingLine((m) => ({ ...m, [k]: true }));
    try {
      const { data } = await axios.patch(
        ENDPOINTS.patchItem(recId, productId),
        payload,
        { headers: { ...getAuthHeaders() } }
      );
      applyUpdatedRecurringToState(data?.recurring || data);
      return true;
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Rivimäärän muutos epäonnistui.');
      return false;
    } finally {
      setPendingLine((m) => {
        const cp = { ...m };
        delete cp[k];
        return cp;
      });
    }
  }

  async function handleSave(rec) {
    const p = pending[rec._id] || {};
    const frequencyWeeks = clampWeeks(p.weeks ?? rec.frequencyWeeks ?? 4);
    const startDateISO = isoWeekToSundayISO(p.weekStr || dateToISOWeekStringLocal(rec.nextOrderDate || new Date()));

    setSavingId(rec._id);
    setError('');

    // Laske diff määrille
    const currentRec = rows.find(r => r._id === rec._id) || rec;
    const currentArr = Array.isArray(currentRec.products) ? currentRec.products
                     : Array.isArray(currentRec.items)    ? currentRec.items : [];
    const curMap = {};
    currentArr.forEach(it => {
      const pid = readProductId(it);
      if (pid) curMap[pid] = readQty(it);
    });

    const origMap = originalQty[rec._id] || {};
    const productIds = Array.from(new Set([...Object.keys(origMap), ...Object.keys(curMap)]));
    const changes = productIds
      .map(pid => ({ pid, nextQty: curMap[pid] ?? 0, prevQty: origMap[pid] ?? 0 }))
      .filter(x => x.nextQty !== x.prevQty);

    // 1) Päivitä rivit (set nextQty). Aja järjestyksessä, jotta virhe pysäyttää.
    try {
      for (const ch of changes) {
        const ok = await patchRecurringItem(rec._id, ch.pid, { action: 'set', value: ch.nextQty });
        if (!ok) throw new Error('Line patch failed');
      }
    } catch (e) {
      setSavingId(null);
      return;
    }

    // 2) Optimistinen seuraava lähetyspäivä UI:lle
    const anchorSunday = parseISODate(startDateISO);
    const previewNext = nextFrom(anchorSunday, frequencyWeeks, new Date());
    const optimistic = { ...rec, frequencyWeeks, nextOrderDate: previewNext.toISOString() };
    setRows((prev) => prev.map((x) => (x._id === rec._id ? optimistic : x)));

    // 3) Päivitä aikataulu backendissä
    try {
      const { data } = await axios.patch(
        ENDPOINTS.update(rec._id),
        { frequencyWeeks, startDateISO },
        { headers: { ...getAuthHeaders() } }
      );
      const updated = data?.recurring || data;
      if (updated && updated._id) {
        setRows((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));

        // Päivitä lähtötila ja nollaa dirty
        setOriginalQty((oq) => ({ ...oq, [rec._id]: curMap }));
        setDirty((d) => { const cp = { ...d }; delete cp[rec._id]; return cp; });

        const anchor = updated.anchorDate || updated.nextOrderDate || anchorSunday;
        setPending((pp) => ({
          ...pp,
          [rec._id]: {
            weeks: Number.isFinite(updated.frequencyWeeks) ? updated.frequencyWeeks : frequencyWeeks,
            weekStr: dateToISOWeekStringLocal(anchor),
          },
        }));
        setToast('Asetukset tallennettu.');
      }
    } catch (e) {
      setRows((prev) => prev.map((x) => (x._id === rec._id ? rec : x)));
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Tallennus epäonnistui.');
    } finally {
      setSavingId(null);
    }
  }

  async function handleCancel(rec) {
    const ok = window.confirm('Poistetaanko toistuva tilaus?\nLähetyksiä ei enää tehdä automaattisesti.');
    if (!ok) return;
    setDeletingId(rec._id);
    setError('');
    setRows((prev) => prev.filter((x) => x._id !== rec._id));
    try {
      await axios.delete(ENDPOINTS.remove(rec._id), { headers: { ...getAuthHeaders() } });
      setToast('Toistuva tilaus poistettu.');
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Poisto epäonnistui.');
      setRows((prev) => [rec, ...prev]);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section className="ro-section" aria-busy={loading ? 'true' : 'false'}>
      <header className="ro-header">
        <h2 className="ro-title">Tilaa automaattisesti – säästä aikaa ja rahaa</h2>
        <p className="ro-subtitle">Muokkaa toimitusväliä. Veloitus tapahtuu vasta, kun tilaus lähtee.</p>
      </header>

      {loading && <p className="ro-loading">Ladataan…</p>}
      <ErrorBanner message={error} onRetry={!loading ? () => user && loadList(user.id || user._id) : undefined} />

      {!loading && !hasAny && !error && (
        <p className="ro-empty">Ei vielä toistuvia tilauksia. Voit lisätä toistuvan tilauksen kassalla.</p>
      )}

      <div className="ro-list" aria-live="polite">
        {rows.map((rec) => {
          const sup = supplierInfo[String(rec.supplierId)] || rec.supplier || {};
          const supplierLabel = sup.displayName || rec.supplierName || sup.name || 'Toimittaja';

          // Tuoterivit
          const items = Array.isArray(rec.products) ? rec.products : Array.isArray(rec.items) ? rec.items : [];
          const linesRaw = items.map((it, i) => {
            const id = readProductId(it);
            const qty = readQty(it);
            const unitCents = readUnitPriceCents(it, productCache);
            return {
              id: id || it.productId || it._id || i,
              pid: id || it.productId || null,  // productId backendille
              canEdit: !!(id || it.productId),
              name: readName(it, productCache),
              pack: readPack(it, productCache),
              qty,
              unitCents,
              baseCents: unitCents * qty,
            };
          });

          // Toimitusmaksut
          const deliveryFeeCents = sup?.deliveryFee != null ? euroLikeToCents(sup.deliveryFee) : 0;
          const freeThresholdCents =
            sup?.freeDeliveryThreshold != null ? euroLikeToCents(sup.freeDeliveryThreshold) : Infinity;

          // Yksinkertaistettu alennus: -4 %
          const ORDER_TYPE_DISCOUNT_PCT = 4;
          const effectiveDiscountPct = ORDER_TYPE_DISCOUNT_PCT;

          // Subtotal + rivikohtainen alennus
          const productsSubtotalCents = linesRaw.reduce((s, r) => s + r.baseCents, 0);
          const lines = linesRaw.map((r) => {
            const lineDiscount = Math.round(r.baseCents * (effectiveDiscountPct / 100));
            const after = Math.max(0, r.baseCents - lineDiscount);
            return { ...r, lineDiscountCents: lineDiscount, afterCents: after };
          });

          // Summat
          const discountAmountCents = lines.reduce((s, r) => s + r.lineDiscountCents, 0);
          const afterDiscountsCents = Math.max(productsSubtotalCents - discountAmountCents, 0);
          const appliedDeliveryFeeCents =
            Number.isFinite(freeThresholdCents) && afterDiscountsCents >= freeThresholdCents ? 0 : deliveryFeeCents;
          const finalTotalCents = afterDiscountsCents + appliedDeliveryFeeCents;

          // Ilmainen toimitus -progress
          const reachedFree = Number.isFinite(freeThresholdCents) && afterDiscountsCents >= freeThresholdCents;
          const missingToFreeCents = Number.isFinite(freeThresholdCents)
            ? Math.max(0, freeThresholdCents - afterDiscountsCents)
            : 0;
          const progressPct = Number.isFinite(freeThresholdCents) && freeThresholdCents > 0
            ? Math.max(0, Math.min(100, (afterDiscountsCents / freeThresholdCents) * 100))
            : 0;

          // Aikataulu
          const p = pending[rec._id] || { weeks: rec.frequencyWeeks || 4, weekStr: dateToISOWeekStringLocal(rec.nextOrderDate || new Date()) };
          const weeks = clampWeeks(p.weeks);
          const anchorSundayISO = isoWeekToSundayISO(p.weekStr);
          const nextDate = nextFrom(parseISODate(anchorSundayISO), weeks, new Date());
          const upcoming = nextOccurrences(anchorSundayISO, weeks, 4);

          const disabled = (savingId === rec._id) || (deletingId === rec._id) || (rec.status === 'cancelled');
          // Onko aikataulussa tai määrissä muutoksia?
          const scheduleDirty =
            weeks !== (rec.frequencyWeeks || 4) ||
            anchorSundayISO !== isoWeekToSundayISO(
              dateToISOWeekStringLocal(rec.nextOrderDate || new Date())
            );
          const hasUnsaved = !!dirty[rec._id] || scheduleDirty;

          return (
            <div key={rec._id} className="ro-card">
              <div className="ro-card__top">
                <div className="ro-card__top-left">
                  <div className="ro-supplier">{supplierLabel}</div>
                  <div className="ro-next" aria-live="polite">
                    Seuraava lähetys (tallennetuin asetuksin): <strong>{fmtDate(rec.nextOrderDate)}</strong>
                  </div>
                </div>
                <div className="ro-card__top-right">
                  <div className="ro-total">{centsToEUR(finalTotalCents)}</div>
                  <StatusBadge status={rec.status} />
                </div>
              </div>

              {/* Tuotteet + alennukset & toimitus samassa taulukossa */}
              <Collapsible labelClosed={`Näytä erittely (${lines.length} tuotetta)`} labelOpen="Piilota erittely">
                <div className="ro-table">
                  <div className="ro-tr ro-head">
                    <div className="ro-td ro-col-name">Tuote</div>
                    <div className="ro-td ro-col-qty">Määrä</div>
                    <div className="ro-td ro-col-unit">á-hinta</div>
                    <div className="ro-td ro-col-disc">Alennus</div>
                    <div className="ro-td ro-col-sum">Rivisumma</div>
                  </div>

                  {lines.map((r) => {
                    const k = keyFor(rec._id, r.pid);
                    const qtyDisabled = disabled || !!pendingLine[k] || !r.canEdit;
                    return (
                      <div key={r.id} className="ro-tr ro-item">
                        <div className="ro-td ro-col-name">
                          <div className="ro-item__name">{r.name}</div>
                          {!!r.pack && <div className="ro-item__pack">Pakkauskoko: {r.pack}</div>}
                        </div>

                        <div className="ro-td ro-col-qty">
                          {r.canEdit ? (
                            <div className="ro-qtyctl" aria-busy={qtyDisabled ? 'true' : 'false'}>
                              <span className="ro-qtypill">
                                <button
                                  type="button"
                                  className="ro-qtybtn"
                                  disabled={qtyDisabled}
                                  onClick={() => handleIncQty(rec, r.pid, -1, r.qty)}
                                  aria-label="Vähennä määrää"
                                >−</button>
                                <span className="ro-qtynum">{r.qty}</span>
                                <button
                                  type="button"
                                  className="ro-qtybtn"
                                  disabled={qtyDisabled}
                                  onClick={() => handleIncQty(rec, r.pid, +1, r.qty)}
                                  aria-label="Lisää määrää"
                                >+</button>
                              </span>

                              <button
                                type="button"
                                className="ro-remove-line"
                                disabled={qtyDisabled}
                                onClick={() => handleRemoveLine(rec, r.pid)}
                                aria-label="Poista rivi"
                              >
                                Poista
                              </button>
                            </div>
                          ) : (
                            <>x{r.qty}</>
                          )}
                        </div>


                        <div className="ro-td ro-col-unit">{centsToEUR(r.unitCents)}</div>
                        <div className="ro-td ro-col-disc">-{centsToEUR(r.lineDiscountCents)}</div>
                        <div className="ro-td ro-col-sum"><strong>{centsToEUR(r.afterCents)}</strong></div>
                      </div>
                    );
                  })}

                  {/* Yhteenveto-rivit */}
                  <div className="ro-tr ro-sep"></div>

                  <div className="ro-tr">
                    <div className="ro-td ro-col-name label">Tuotteet (ennen alennuksia)</div>
                    <div className="ro-td ro-col-qty"></div>
                    <div className="ro-td ro-col-unit"></div>
                    <div className="ro-td ro-col-disc"></div>
                    <div className="ro-td ro-col-sum">{centsToEUR(productsSubtotalCents)}</div>
                  </div>

                  {effectiveDiscountPct > 0 && (
                    <>
                      <div className="ro-tr">
                        <div className="ro-td ro-col-name label">Alennukset yhteensä</div>
                        <div className="ro-td ro-col-qty"></div>
                        <div className="ro-td ro-col-unit"></div>
                        <div className="ro-td ro-col-disc"></div>
                        <div className="ro-td ro-col-sum">-{centsToEUR(discountAmountCents)}</div>
                      </div>

                      <div className="ro-tr sub">
                        <div className="ro-td ro-col-name">
                          • Tilaustapa-alennus: Toistuva tilaus −{effectiveDiscountPct.toFixed(0)} %
                        </div>
                        <div className="ro-td ro-col-qty"></div>
                        <div className="ro-td ro-col-unit"></div>
                        <div className="ro-td ro-col-disc"></div>
                        <div className="ro-td ro-col-sum">-{centsToEUR(discountAmountCents)}</div>
                      </div>
                    </>
                  )}

                  <div className="ro-tr">
                    <div className="ro-td ro-col-name label">Toimitusmaksu</div>
                    <div className="ro-td ro-col-qty"></div>
                    <div className="ro-td ro-col-unit"></div>
                    <div className="ro-td ro-col-disc"></div>
                    <div className="ro-td ro-col-sum">{centsToEUR(appliedDeliveryFeeCents)}</div>
                  </div>

                  <div className="ro-tr final">
                    <div className="ro-td ro-col-name label">Yhteensä ({supplierLabel})</div>
                    <div className="ro-td ro-col-qty"></div>
                    <div className="ro-td ro-col-unit"></div>
                    <div className="ro-td ro-col-disc"></div>
                    <div className="ro-td ro-col-sum"><strong>{centsToEUR(finalTotalCents)}</strong></div>
                  </div>

                  {Number.isFinite(freeThresholdCents) && (
                    <div className="ro-free">
                      <div className="ro-free__track">
                        <div className="ro-free__fill" style={{ width: `${progressPct}%` }} />
                      </div>
                      <div className="ro-free__note">
                        {reachedFree
                          ? 'Ilmainen toimitus saavutettu.'
                          : <>Ilmaiseen toimitukseen: <strong>{centsToEUR(missingToFreeCents)}</strong></>}
                        {' '} (toimitus {centsToEUR(deliveryFeeCents)} alle {centsToEUR(freeThresholdCents)} tilauksille)
                      </div>
                    </div>
                  )}
                </div>
              </Collapsible>

              {/* Ohjaimet + aikataulu */}
              <div className="ro-controls">
                <div className="ro-controls__grid">
                  <label className="ro-field">
                    <span className="ro-label">Ensimmäinen toimitusviikko</span>
                    <input
                      type="week"
                      value={p.weekStr}
                      onChange={(e) => onChangeWeekStr(rec._id, e.target.value)}
                      disabled={disabled}
                      className="ro-input"
                    />
                    <small className="ro-help">
                      Lähetys tehdään sunnuntaisin. Tallennettaessa sunnuntai: <strong>{fmtDate(anchorSundayISO)}</strong>
                    </small>
                  </label>

                  <label className="ro-field">
                    <span className="ro-label">Toistuvuus</span>
                    <WeeksSelect value={weeks} onChange={(val) => onChangeWeeks(rec._id, val)} disabled={disabled} />
                    <small className="ro-help">Valitse 1–12 viikkoa. Lähetys lähtee aina sunnuntaisin.</small>
                  </label>
                </div>

                <div className="ro-schedule" aria-live="polite">
                  <div className="ro-schedule__row">
                    <span className="ro-schedule__label">Seuraava lähetys näillä asetuksilla:</span>
                    <strong>{fmtDate(nextDate)}</strong>
                  </div>
                  <div className="ro-schedule__row">
                    <span className="ro-schedule__label">Seuraavaksi tulossa:</span>
                    <ul className="ro-schedule__chips">
                      {upcoming.map((d, idx) => (
                        <li key={idx} className="ro-chip ro-chip--sm">{fmtDate(d)}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="ro-actions">
                  <button
                    type="button"
                    onClick={() => handleSave(rec)}
                    disabled={disabled}
                    className="ro-btn ro-btn--primary"
                  >
                    {savingId === rec._id ? 'Tallennetaan…' : 'Tallenna asetukset'}
                  </button>
                  {hasUnsaved && <span className="ro-unsaved">Tallentamattomia muutoksia</span>}
                  <button
                    type="button"
                    onClick={() => handleCancel(rec)}
                    disabled={disabled}
                    className="ro-btn ro-btn--danger"
                  >
                    {deletingId === rec._id ? 'Poistetaan…' : 'Poista toistuva tilaus'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Toast />
    </section>
  );
}
